import {
    ApiQuery,
    FilterOperators,
    PaginationQuery,
    SortDirections,
    SortQuery,
} from "~/modules/filters/types";
import { AssetColumns } from "~/modules/assets/types";
import {
    AssetTableChartConfig,
    AssetTableColumnConfig,
    ChartConfig,
} from "~/modules/charts/types";
import {
    isAssetTableColumnSlug,
    MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN,
} from "./assetTableColumns";

// // // //

export const ASSET_TABLE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const DEFAULT_ASSET_TABLE_ROWS_PER_PAGE = 25;

/**
 * DEFAULT_ASSET_TABLE_COLUMN_WIDTH
 * The width to start a column at when its definition declares none.
 */
export const DEFAULT_ASSET_TABLE_COLUMN_WIDTH = 500;

/**
 * getDefaultColumnWidth
 * The width a column starts at, in pixels, before anyone resizes it: the one
 * its own `ColumnMetadata` declares, so a column added from the picker opens
 * at the width it was designed for rather than a one-size-fits-all guess.
 */
export function getDefaultColumnWidth(slug: AssetColumns): number {
    return (
        MAP_ASSET_COLUMN_TO_ASSET_TABLE_COLUMN[slug]?.width ??
        DEFAULT_ASSET_TABLE_COLUMN_WIDTH
    );
}

/**
 * DEFAULT_ASSET_TABLE_COLUMNS
 * The columns a new asset table starts with. The asset name column is always
 * rendered first and is therefore not listed here.
 */
export const DEFAULT_ASSET_TABLE_COLUMNS: AssetTableColumnConfig[] = [
    AssetColumns.assetClassName,
    AssetColumns.networkIDs,
    AssetColumns.bridgedTokenValueDollar,
    AssetColumns.holdingAddressesCount,
].map((slug) => ({ slug, width: getDefaultColumnWidth(slug) }));

/**
 * DEFAULT_ASSET_TABLE_SORT
 * The ordering a new asset table opens on - largest first, by the same measure
 * the default columns lead with.
 */
export const DEFAULT_ASSET_TABLE_SORT: SortQuery = {
    direction: SortDirections.desc,
    field: AssetColumns.bridgedTokenValueDollar,
};

export const DEFAULT_ASSET_TABLE_PAGINATION: PaginationQuery = {
    page: 1,
    perPage: DEFAULT_ASSET_TABLE_ROWS_PER_PAGE,
};

export const DEFAULT_ASSET_TABLE_QUERY: ApiQuery = {
    filter: {
        operator: FilterOperators.And,
        filters: [],
    },
    sort: DEFAULT_ASSET_TABLE_SORT,
    pagination: DEFAULT_ASSET_TABLE_PAGINATION,
};

export const DEFAULT_ASSET_TABLE_CONFIG: AssetTableChartConfig = {
    query: DEFAULT_ASSET_TABLE_QUERY,
    table_columns: DEFAULT_ASSET_TABLE_COLUMNS,
};

// // // //

/**
 * parseTableColumns
 * Reads the stored column list, dropping anything that is not a column the
 * asset table knows how to render (an unknown slug from an older config, or a
 * malformed entry) so a stale config still renders the columns it can.
 */
function parseTableColumns(value: unknown): AssetTableColumnConfig[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.reduce<AssetTableColumnConfig[]>((accumulated, entry) => {
        const { slug, width } =
            (entry as AssetTableColumnConfig | undefined) ?? {};

        if (isAssetTableColumnSlug(slug)) {
            accumulated.push({
                slug,
                width: width ?? getDefaultColumnWidth(slug),
            });
        }

        return accumulated;
    }, []);
}

/**
 * parseQuery
 * Reads the stored query, backfilling `sort` and `pagination`. Both are
 * required on `ApiQuery` but a hand-built or URL-seeded config can arrive
 * without them, and the sort controls read `query.sort` unconditionally.
 */
function parseQuery(value: unknown): ApiQuery {
    if (!value || typeof value !== "object") {
        return DEFAULT_ASSET_TABLE_QUERY;
    }

    const query = value as ApiQuery;

    // Returned by reference when there is nothing to backfill: the renderer
    // re-seeds its viewer-facing paging whenever this query's identity changes,
    // so a fresh object on every call would reset the page on any config edit.
    if (query.sort && query.pagination) {
        return query;
    }

    return {
        ...query,
        sort: query.sort ?? DEFAULT_ASSET_TABLE_SORT,
        pagination: query.pagination ?? DEFAULT_ASSET_TABLE_PAGINATION,
    };
}

/**
 * toAssetTableChartConfig
 * Normalizes a loose chart config object into an asset table config shape.
 */
export function toAssetTableChartConfig(
    value: ChartConfig,
): AssetTableChartConfig {
    return {
        query: parseQuery(value.query),
        table_columns: parseTableColumns(value.table_columns),
    };
}

/**
 * toAssetTableColumnSlugs
 * Flattens a config's columns into the slug list the column selector edits.
 */
export function toAssetTableColumnSlugs(
    tableColumns: AssetTableColumnConfig[],
): AssetColumns[] {
    return tableColumns.map((tableColumn) => tableColumn.slug);
}

/**
 * toAssetTableColumnConfigs
 * Inverse of `toAssetTableColumnSlugs` - wraps the selector's slugs back into
 * the object form stored on the config.
 *
 * The selector only edits which columns are shown and in what order, so the
 * config each slug already had is carried over: a column that survives an edit
 * keeps the width it was resized to, and only genuinely new columns take the
 * width their definition declares. Pass the config's current `table_columns`
 * as `previousColumns` - without them every edit would reset every width.
 */
export function toAssetTableColumnConfigs(
    slugs: AssetColumns[],
    previousColumns: AssetTableColumnConfig[],
): AssetTableColumnConfig[] {
    const previousBySlug = new Map(
        previousColumns.map((tableColumn) => [tableColumn.slug, tableColumn]),
    );

    return slugs.map((slug) => ({
        ...previousBySlug.get(slug),
        slug,
        width: previousBySlug.get(slug)?.width ?? getDefaultColumnWidth(slug),
    }));
}
